import axios from 'axios';
// Create axios instance with base URL from environment variables
const baseURL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  }
  // // Enforce secure connections in production
  // withCredentials: isProduction,
});

// Add a request interceptor to add the token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Authentication services
const AuthService = {
  // Register a new user
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        try {
          // Try to safely set items in localStorage
          localStorage.setItem('token', response.data.token);
          // Store user info
          localStorage.setItem('user', JSON.stringify(response.data.user || {}));
        } catch (storageError) {
          console.error('Error storing auth data:', storageError);
          // If localStorage fails, still return the data but log the error
          
          // Handle potential localStorage errors in mobile browsers in incognito mode
          // by creating fallbacks if needed
          if (!window.__authData) {
            window.__authData = {};
          }
          window.__authData.token = response.data.token;
          window.__authData.user = response.data.user || {};
        }
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Logout user
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Get current user from token
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Check if user is admin
  isAdmin: () => {
    const user = AuthService.getCurrentUser();
    return user && user.role === 'admin';
  }
};

// User services
const UserService = {
  // Get user profile
  getProfile: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      const response = await api.put('/users/profile', userData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
};

// Event services
const EventService = {
  // Get all events
  getAllEvents: async () => {
    try {
      const response = await api.get('/events');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get a specific event
  getEvent: async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Get event image - Note that this endpoint returns binary image data directly
  // This function is not used directly in components since we use the URL directly in img src
  getEventImage: async (eventId) => {
    try {
      // Set responseType to 'blob' to handle binary data
      const response = await api.get(`/events/image/${eventId}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Create a new event
  createEvent: async (eventData) => {
    // Create FormData for file upload
    const formData = new FormData();
    
    // Append all data to FormData
    Object.keys(eventData).forEach(key => {
      if (key === 'image' && eventData[key] instanceof File) {
        formData.append(key, eventData[key]);
      } else {
        formData.append(key, eventData[key]);
      }
    });

    try {
      const response = await api.post('/admin/events', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update an event
  updateEvent: async (eventId, eventData) => {
    // Create FormData for file upload
    const formData = new FormData();
    
    // Append all data to FormData
    Object.keys(eventData).forEach(key => {
      if (key === 'image' && eventData[key] instanceof File) {
        formData.append(key, eventData[key]);
      } else {
        formData.append(key, eventData[key]);
      }
    });

    try {
      const response = await api.put(`/admin/events/${eventId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Delete an event
  deleteEvent: async (eventId) => {
    try {
      const response = await api.delete(`/admin/events/${eventId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Register for an event
  registerForEvent: async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/register`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get events the current user has registered for
  getMyEvents: async () => {
    try {
      const response = await api.get('/events/my');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
};

// Banner services
const BannerService = {
  // Get all banners
  getAllBanners: async () => {
    try {
      const response = await api.get('/banners');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get a specific banner
  getBanner: async (bannerId) => {
    try {
      const response = await api.get(`/banners/${bannerId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Get banner image
  getBannerImage: async (bannerId) => {
    try {
      const response = await api.get(`/banners/image/${bannerId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Create a new banner
  createBanner: async (bannerData) => {
    // Create FormData for file upload
    const formData = new FormData();
    
    // Append all data to FormData
    Object.keys(bannerData).forEach(key => {
      if (key === 'image' && bannerData[key] instanceof File) {
        formData.append(key, bannerData[key]);
      } else {
        formData.append(key, bannerData[key]);
      }
    });

    try {
      const response = await api.post('/admin/banners', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update a banner
  updateBanner: async (bannerId, bannerData) => {
    // Create FormData for file upload
    const formData = new FormData();
    
    // Append all data to FormData
    Object.keys(bannerData).forEach(key => {
      if (key === 'image' && bannerData[key] instanceof File) {
        formData.append(key, bannerData[key]);
      } else {
        formData.append(key, bannerData[key]);
      }
    });

    try {
      const response = await api.put(`/admin/banners/${bannerId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Delete a banner
  deleteBanner: async (bannerId) => {
    try {
      const response = await api.delete(`/admin/banners/${bannerId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
};

// Success Story services
const StoryService = {
  // Get all success stories
  getAllStories: async () => {
    try {
      const response = await api.get('/stories');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get a specific success story
  getStory: async (storyId) => {
    try {
      const response = await api.get(`/stories/${storyId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Get story image
  getStoryImage: async (storyId) => {
    try {
      const response = await api.get(`/stories/image/${storyId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Create a new success story (admin only)
  createStory: async (storyData) => {
    
    try {
      // We'll use the FormData directly as provided from the component
      // This matches the curl request format
      const response = await api.post('/admin/stories', storyData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Story creation error:', error.response?.data || error.message);
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update a success story (admin only)
  updateStory: async (storyId, storyData) => {
    
    try {
      // We'll use the FormData directly as provided from the component
      // This matches the curl request format
      const response = await api.put(`/admin/stories/${storyId}`, storyData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Story update error:', error.response?.data || error.message);
      throw error.response ? error.response.data : error.message;
    }
  },

  // Delete a success story (admin only)
  deleteStory: async (storyId) => {
    try {
      const response = await api.delete(`/admin/stories/${storyId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
};

// Admin services
const AdminService = {
  // Get all users (admin only)
  getAllUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get a specific user (admin only)
  getUser: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update user data (admin only)
  updateUser: async (userId, userData) => {
    // Validate userId to prevent "undefined" in API URL
    if (!userId) {
      console.error("Cannot update user: User ID is undefined or null");
      throw new Error("User ID is required for this operation");
    }
    
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error(`API Error: ${error.response?.status || 'Unknown'} - ${error.response?.statusText || error.message}`);
      throw error;
    }
  },

  // Update user role (admin only)
  updateUserRole: async (userId, role) => {
    // Validate userId to prevent "undefined" in API URL
    if (!userId) {
      console.error("Cannot update user role: User ID is undefined or null");
      throw new Error("User ID is required for this operation");
    }
    
    try {
      const response = await api.put(`/admin/users/${userId}`, { role });
      return response.data;
    } catch (error) {
      console.error(`API Error: ${error.response?.status || 'Unknown'} - ${error.response?.statusText || error.message}`);
      throw error;
    }
  },

  // Update user status (admin only)
  updateUserStatus: async (userId, status) => {
    // Validate userId to prevent "undefined" in API URL
    if (!userId) {
      console.error("Cannot update user status: User ID is undefined or null");
      throw new Error("User ID is required for this operation");
    }
    
    try {
      const response = await api.put(`/admin/users/${userId}`, { status });
      return response.data;
    } catch (error) {
      console.error(`API Error: ${error.response?.status || 'Unknown'} - ${error.response?.statusText || error.message}`);
      throw error;
    }
  },

  // Delete user (admin only)
  deleteUser: async (userId) => {
    // Validate userId to prevent "undefined" in API URL
    if (!userId) {
      console.error("Cannot delete user: User ID is undefined or null");
      throw new Error("User ID is required for this operation");
    }
    
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`API Error: ${error.response?.status || 'Unknown'} - ${error.response?.statusText || error.message}`);
      throw error;
    }
  },

  // Get admin dashboard data
  getDashboardData: async () => {
    try {
      const response = await api.get('/admin/dashboard');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get the shared certificate background image (returns { imageData: "data:..." } or { imageData: null })
  getCertBgImage: async () => {
    try {
      const response = await api.get('/admin/cert-bg');
      return response.data;
    } catch (error) {
      return { imageData: null };
    }
  },

  // Upload / overwrite the shared certificate background image on the server
  setCertBgImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/admin/cert-bg', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },
};

// Event Registration services
const EventRegistrationService = {
  // Register for an event
  registerForEvent: async (eventId, notes) => {
    try {
      const response = await api.post('/event-registrations', { event_id: eventId, notes });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get all registrations for the current user
  getMyRegistrations: async () => {
    try {
      const response = await api.get('/event-registrations/my-registrations');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Cancel registration for an event
  cancelRegistration: async (registrationId) => {
    try {
      const response = await api.delete(`/event-registrations/${registrationId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Submit feedback for an event
  submitFeedback: async (registrationId, feedback, rating) => {
    try {
      const response = await api.post(`/event-registrations/${registrationId}/feedback`, { feedback, rating });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: Get all registrations for a specific event
  getEventRegistrations: async (eventId) => {
    try {
      const response = await api.get(`/admin/events/${eventId}/registrations`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: Update registration status
  updateRegistrationStatus: async (registrationId, status) => {
    try {
      const response = await api.put(`/admin/registrations/${registrationId}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: Check in an attendee
  checkInAttendee: async (registrationId) => {
    try {
      const response = await api.post(`/admin/registrations/${registrationId}/check-in`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: Delete a registration
  deleteRegistration: async (registrationId) => {
    try {
      const response = await api.delete(`/admin/registrations/${registrationId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: Get all registrations across all events
  // This method is no longer used, we're using getEventRegistrations instead
  getAllRegistrations: async () => {
    try {
      throw new Error('This method should not be used directly. Use getEventRegistrations with specific event IDs instead.');
    } catch (error) {
      throw error.message;
    }
  }
};

// Member services
const MemberService = {
  // Get all members
  getAllMembers: async () => {
    try {
      const response = await api.get('/members');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get member by ID
  getMember: async (memberId) => {
    try {
      const response = await api.get(`/members/${memberId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Create a new member with image
  createMember: async (memberData) => {
    try {
      const formData = new FormData();
      formData.append('name', memberData.name);
      formData.append('member_role', memberData.member_role);
      formData.append('email', memberData.email);
      if (memberData.image) {
        formData.append('image', memberData.image);
      }

      const response = await api.post('/admin/members', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update member
  updateMember: async (memberId, memberData) => {
    try {
      const formData = new FormData();
      if (memberData.name) formData.append('name', memberData.name);
      if (memberData.member_role) formData.append('member_role', memberData.member_role);
      if (memberData.email) formData.append('email', memberData.email);
      if (memberData.image) {
        formData.append('image', memberData.image);
      }

      const response = await api.put(`/admin/members/${memberId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Delete member
  deleteMember: async (memberId) => {
    try {
      const response = await api.delete(`/admin/members/${memberId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
};

const DocumentService = {
  // Get all documents
  getAllDocuments: async () => {
    try {
      const response = await api.get('/documents');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Get document by ID
  getDocument: async (documentId) => {
    try {
      const response = await api.get(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Create a new document with file
  createDocument: async (documentData) => {
    try {
      const formData = new FormData();
      formData.append('title', documentData.title);
      formData.append('document_type', documentData.document_type);
      if (documentData.file) {
        formData.append('file', documentData.file);
      }

      const response = await api.post('/admin/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Update document
  updateDocument: async (documentId, documentData) => {
    try {
      const formData = new FormData();
      if (documentData.title) formData.append('title', documentData.title);
      if (documentData.document_type) formData.append('document_type', documentData.document_type);
      if (documentData.file) {
        formData.append('file', documentData.file);
      }

      const response = await api.put(`/admin/documents/${documentId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Delete document
  deleteDocument: async (documentId) => {
    try {
      const response = await api.delete(`/admin/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
};

const CertificateService = {
  // Admin: save certificate record to DB
  create: async (certData) => {
    try {
      const response = await api.post('/certificates', certData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: get all certificates
  getAll: async () => {
    try {
      const response = await api.get('/certificates');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Admin: delete a certificate
  delete: async (id) => {
    try {
      const response = await api.delete(`/certificates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // User: get my certificates
  getMy: async () => {
    try {
      const response = await api.get('/certificates/my');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },

  // Public: verify by cert_no
  verify: async (certNo) => {
    try {
      const response = await api.get(`/certificates/verify/${certNo}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  },
};

export { api, AuthService, UserService, EventService, BannerService, StoryService, AdminService, EventRegistrationService, MemberService, DocumentService, CertificateService, baseURL};
